import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Camera, PackagePlus, Repeat, Truck, ClipboardCheck, CheckCircle2, XCircle } from "lucide-react";

export default function Warehouse() {
  const { user } = useAuth();
  const [tab, setTab] = useState("receive");
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [activities, setActivities] = useState([]);

  const refresh = async () => {
    const [p, b, a] = await Promise.all([
      api.get("/products"),
      api.get("/branches"),
      api.get("/activities?limit=20"),
    ]);
    setProducts(p.data); setBranches(b.data); setActivities(a.data);
  };
  useEffect(() => { refresh(); }, []);

  const raw = products.filter((p) => p.kind === "bahan_baku");
  const jadi = products.filter((p) => p.kind === "produk_jadi");

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Modul Gudang Pusat</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Operasi Gudang</h1>
        <p className="text-sm text-slate-600 mt-1">Semua aksi wajib disertai foto bukti.</p>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto no-scrollbar">
        {[
          { id: "receive", label: "Bahan Masuk", icon: PackagePlus },
          { id: "repack", label: "Repack", icon: Repeat },
          { id: "ship", label: "Kirim Cabang", icon: Truck },
          { id: "pending", label: "Menunggu ACC", icon: ClipboardCheck },
        ].map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex-1 min-w-max h-11 px-4 rounded-lg flex items-center justify-center gap-2 text-sm font-bold transition-colors ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "receive" && <ReceiveForm products={raw} onDone={refresh} />}
      {tab === "repack" && <RepackForm raw={raw} jadi={jadi} onDone={refresh} />}
      {tab === "ship" && <ShipForm products={jadi} branches={branches} onDone={refresh} />}
      {tab === "pending" && <PendingList isAdmin={user?.role === "admin"} onDone={refresh} />}

      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-3" style={{fontFamily:'Manrope'}}>Riwayat Aktivitas Gudang</h2>
        <div className="space-y-2">
          {activities.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-3 flex gap-3">
              {a.photo_id && <img src={fileUrl(a.photo_id)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">{a.type?.replace(/_/g, ' ')}</p>
                  {a.status === "pending_approval" && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">Menunggu ACC</span>}
                  {a.status === "approved" && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">ACC</span>}
                  {a.status === "rejected" && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wider">Ditolak</span>}
                </div>
                <p className="text-sm font-semibold text-slate-900 truncate">{a.notes || `Qty: ${a.qty || a.output_qty || ''}`}</p>
                <p className="text-xs text-slate-500">{a.created_by_name} · {new Date(a.created_at).toLocaleString('id-ID')}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReceiveForm({ products, onDone }) {
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("");
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef();
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Foto bukti wajib diambil");
    const fd = new FormData();
    fd.append("product_id", productId); fd.append("qty", qty); fd.append("supplier", supplier); fd.append("notes", notes); fd.append("photo", file);
    const t = toast.loading("Menyimpan...");
    try {
      const { data } = await api.post("/warehouse/receive-raw", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.status === "pending_approval" ? "Tercatat — menunggu ACC admin sebelum masuk stok" : "Bahan baku tercatat & masuk stok", { id: t });
      setProductId(""); setQty(""); setSupplier(""); setNotes(""); setFile(null); fileRef.current.value = "";
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Terima Bahan Baku</h3>
      <Select testid="rec-product" label="Bahan Baku" value={productId} onChange={setProductId} options={products.map(p => ({ value: p.id, label: `${p.name} (${p.unit})` }))} required />
      <Input testid="rec-qty" label="Jumlah (sesuai surat jalan/data supplier)" type="number" value={qty} onChange={setQty} required />
      <Input testid="rec-supplier" label="Nama Supplier (opsional)" value={supplier} onChange={setSupplier} />
      <TextArea testid="rec-notes" label="Catatan" value={notes} onChange={setNotes} />
      <FilePicker testid="rec-photo" label="Foto Bukti (wajib)" file={file} setFile={setFile} inputRef={fileRef} />
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">Stok baru bertambah setelah admin memeriksa fisik barang dan menekan ACC di tab "Menunggu ACC".</p>
      <button data-testid="rec-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl active:scale-[0.98] transition-transform">Simpan</button>
    </form>
  );
}

function RepackForm({ raw, jadi, onDone }) {
  const [rawId, setRawId] = useState(""); const [rawQty, setRawQty] = useState("");
  const [outId, setOutId] = useState(""); const [outQty, setOutQty] = useState("");
  const [notes, setNotes] = useState(""); const [file, setFile] = useState(null);
  const fileRef = useRef();
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Foto bukti wajib");
    const fd = new FormData();
    fd.append("raw_product_id", rawId); fd.append("raw_qty", rawQty);
    fd.append("output_product_id", outId); fd.append("output_qty", outQty);
    fd.append("notes", notes); fd.append("photo", file);
    const t = toast.loading("Menyimpan...");
    try {
      const { data } = await api.post("/warehouse/repack", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.status === "pending_approval" ? "Tercatat — menunggu ACC admin" : "Repack tercatat", { id: t });
      setRawId(""); setRawQty(""); setOutId(""); setOutQty(""); setNotes(""); setFile(null); fileRef.current.value = "";
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Repack Botol Kecil</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select testid="rep-raw" label="Bahan Baku Digunakan" value={rawId} onChange={setRawId} options={raw.map(p => ({ value: p.id, label: p.name }))} required />
        <Input testid="rep-rawqty" label="Qty Bahan" type="number" value={rawQty} onChange={setRawQty} required />
        <Select testid="rep-out" label="Produk Jadi" value={outId} onChange={setOutId} options={jadi.map(p => ({ value: p.id, label: p.name }))} required />
        <Input testid="rep-outqty" label="Qty Produk Jadi" type="number" value={outQty} onChange={setOutQty} required />
      </div>
      <TextArea testid="rep-notes" label="Catatan" value={notes} onChange={setNotes} />
      <FilePicker testid="rep-photo" label="Foto Bukti Repack" file={file} setFile={setFile} inputRef={fileRef} />
      <button data-testid="rep-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl active:scale-[0.98] transition-transform">Simpan</button>
    </form>
  );
}

function ShipForm({ products, branches, onDone }) {
  const [branchId, setBranchId] = useState(""); const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(""); const [notes, setNotes] = useState(""); const [file, setFile] = useState(null);
  const fileRef = useRef();
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Foto bukti wajib");
    const fd = new FormData();
    fd.append("to_branch_id", branchId); fd.append("product_id", productId);
    fd.append("qty", qty); fd.append("notes", notes); fd.append("photo", file);
    const t = toast.loading("Menyimpan...");
    try {
      const { data } = await api.post("/warehouse/ship", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.status === "pending_approval" ? "Tercatat — menunggu ACC admin sebelum dikirim" : "Pengiriman dibuat", { id: t });
      setBranchId(""); setProductId(""); setQty(""); setNotes(""); setFile(null); fileRef.current.value = "";
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Kirim ke Cabang</h3>
      <Select testid="ship-branch" label="Cabang Tujuan" value={branchId} onChange={setBranchId} options={branches.map(b => ({ value: b.id, label: `${b.name} - ${b.city}` }))} required />
      <Select testid="ship-product" label="Produk" value={productId} onChange={setProductId} options={products.map(p => ({ value: p.id, label: p.name }))} required />
      <Input testid="ship-qty" label="Jumlah" type="number" value={qty} onChange={setQty} required />
      <TextArea testid="ship-notes" label="Catatan" value={notes} onChange={setNotes} />
      <FilePicker testid="ship-photo" label="Foto Bukti Kirim" file={file} setFile={setFile} inputRef={fileRef} />
      <button data-testid="ship-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl active:scale-[0.98] transition-transform">Kirim</button>
    </form>
  );
}

// Shared form helpers (kept local to avoid over-engineering)
function PendingList({ isAdmin, onDone }) {
  const [rows, setRows] = useState(null);
  const [qtys, setQtys] = useState({});
  const [rejectId, setRejectId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const load = () => api.get("/warehouse/pending").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const TYPE_LABEL = { bahan_masuk: "Barang Datang", repack: "Repack", shipment_out: "Kirim Cabang" };
  const descOf = (r) => {
    if (r.type === "bahan_masuk") return `${r.product_id_name || ""} ×${r.qty}${r.supplier ? ` · Supplier: ${r.supplier}` : ""}`;
    if (r.type === "repack") return `${r.raw_product_id_name || ""} ×${r.raw_qty} → ${r.output_product_id_name || ""} ×${r.output_qty}`;
    return `${r.product_id_name || ""} ×${r.qty} → ${r.to_branch_name || "cabang"}`;
  };

  const decide = async (r, action) => {
    const payload = { action };
    if (action === "approve" && r.type === "bahan_masuk" && qtys[r.id] !== undefined && qtys[r.id] !== "") payload.actual_qty = parseInt(qtys[r.id]);
    if (action === "reject") payload.notes = rejectNotes;
    const t = toast.loading("Memproses...");
    try {
      await api.patch(`/warehouse/pending/${r.id}`, payload);
      toast.success(action === "approve" ? "Di-ACC — stok diperbarui" : "Ditolak & tercatat di riwayat", { id: t });
      setRejectId(null); setRejectNotes("");
      await load(); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  return (
    <div data-testid="pending-list" className="space-y-3">
      {!isAdmin && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">Operasi Anda menunggu pemeriksaan & ACC admin sebelum stok berubah.</p>}
      {rows === null && <p className="text-sm text-slate-500">Memuat...</p>}
      {rows !== null && rows.length === 0 && <div data-testid="pending-empty" className="bg-white rounded-xl border border-slate-200 p-6 text-center text-sm text-slate-500">Tidak ada operasi menunggu ACC.</div>}
      {(rows || []).map((r) => (
        <div key={r.id} data-testid={`pending-card-${r.id}`} className="bg-white rounded-xl border border-amber-200 p-4">
          <div className="flex gap-3">
            {r.photo_id && <a href={fileUrl(r.photo_id)} target="_blank" rel="noreferrer"><img src={fileUrl(r.photo_id)} alt="" className="w-16 h-16 rounded-lg object-cover" /></a>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">Menunggu ACC</span>
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">{TYPE_LABEL[r.type] || r.type}</span>
              </div>
              <p className="mt-1 text-sm font-semibold text-slate-900">{descOf(r)}</p>
              {r.notes && <p className="text-xs text-slate-600 mt-0.5">{r.notes}</p>}
              <p className="text-xs text-slate-500 mt-0.5">{r.created_by_name} · {new Date(r.created_at).toLocaleString("id-ID")}</p>
            </div>
          </div>
          {isAdmin && (
            <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
              {r.type === "bahan_masuk" && (
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Qty Aktual Diterima (koreksi bila tidak sesuai data supplier)</span>
                  <input data-testid={`pending-qty-${r.id}`} type="number" min={1} value={qtys[r.id] ?? r.qty} onChange={(e) => setQtys({ ...qtys, [r.id]: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
                </label>
              )}
              {rejectId === r.id ? (
                <div className="space-y-2">
                  <input data-testid={`pending-reject-notes-${r.id}`} value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} placeholder="Alasan penolakan (wajib)" className="w-full h-11 px-3 rounded-lg border border-red-300 text-sm" />
                  <div className="flex gap-2">
                    <button data-testid={`pending-reject-confirm-${r.id}`} onClick={() => decide(r, "reject")} className="flex-1 h-10 rounded-lg bg-red-600 text-white text-sm font-bold">Tolak Operasi</button>
                    <button onClick={() => { setRejectId(null); setRejectNotes(""); }} className="h-10 px-3 rounded-lg border border-slate-300 text-sm font-bold text-slate-600">Batal</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button data-testid={`pending-acc-${r.id}`} onClick={() => decide(r, "approve")} className="flex-1 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold flex items-center justify-center gap-1 active:scale-[0.98] transition-transform"><CheckCircle2 size={15} /> ACC & Masukkan Stok</button>
                  <button data-testid={`pending-reject-${r.id}`} onClick={() => setRejectId(r.id)} className="h-11 px-4 rounded-lg border border-red-300 text-red-600 text-sm font-bold flex items-center gap-1"><XCircle size={15} /> Tolak</button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Input({ testid, label, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
      <input data-testid={testid} {...rest} onChange={(e) => rest.onChange(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-base bg-white" />
    </label>
  );
}
function TextArea({ testid, label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-base bg-white" />
    </label>
  );
}
function Select({ testid, label, value, onChange, options, required }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-base">
        <option value="">-- pilih --</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function FilePicker({ testid, label, file, setFile, inputRef, accept = "image/*", capture = "environment" }) {
  return (
    <div>
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
      <input ref={inputRef} data-testid={testid} type="file" accept={accept} capture={capture} hidden onChange={(e) => setFile(e.target.files[0])} />
      <button type="button" onClick={() => inputRef.current.click()} className="mt-1 w-full h-16 border-2 border-dashed border-blue-300 bg-blue-50 text-blue-700 rounded-xl flex flex-col items-center justify-center gap-1 active:bg-blue-100 transition-colors">
        <Camera size={20} />
        <span className="text-sm font-semibold">{file ? file.name : "Ambil Foto / Pilih File"}</span>
      </button>
      {file && file.type?.startsWith("image/") && (
        <img src={URL.createObjectURL(file)} alt="preview" className="mt-2 w-full h-40 object-cover rounded-lg border border-slate-200" />
      )}
    </div>
  );
}
export { Input, TextArea, Select, FilePicker };
